import { a2aService, type A2aBalanceResponse } from '@/services/a2a.service';
import { rememberAlps2AlpsUserId } from '@/services/gtm-ecommerce.service';
import { getCheckoutSession } from '@/services/orders.service';
import { CHECKOUT_STORAGE_KEY } from '@/shared/constants/storage-keys';
import { useCallback, useEffect, useRef, useState } from 'react';

type A2aPaymentState = {
  authenticated: boolean;
  balance: number | null;
  currency: string | null;
  email: string | null;
  /** This customer's loyalty runs on MAM Engine: limits come from `maxRedeemable`, not a percent. */
  engine: boolean;
  memberExists: boolean;
  maxRedeemable: number | null;
  blockedBy: string | null;
  minOrderValue: number | null;
  loading: boolean;
  loginError: string | null;
  payError: string | null;
  paySuccess: boolean;
  paying: boolean;
  appliedPoints: number | null;
};

const A2A_LOGIN_FAILED_MESSAGE = 'A2A login failed';
const A2A_APPLIED_POINTS_PREFIX = `${CHECKOUT_STORAGE_KEY}:a2a-applied-points:`;

function getAppliedPointsStorageKey(): string | null {
  const checkout = getCheckoutSession();
  if (!checkout) {
    return null;
  }

  return `${A2A_APPLIED_POINTS_PREFIX}${String(checkout.orderId)}`;
}

function readPersistedAppliedPoints(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const key = getAppliedPointsStorageKey();
  if (!key) {
    return null;
  }

  const raw = window.sessionStorage.getItem(key);
  if (!raw) {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return Math.round(parsed * 100) / 100;
}

function persistAppliedPoints(points: number | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  const key = getAppliedPointsStorageKey();
  if (!key) {
    return;
  }

  if (points == null || !Number.isFinite(points) || points < 0) {
    window.sessionStorage.removeItem(key);
    return;
  }

  window.sessionStorage.setItem(key, String(Math.round(points * 100) / 100));
}

function walletToState(wallet: A2aBalanceResponse): Partial<A2aPaymentState> {
  return {
    authenticated: wallet.authenticated,
    balance: wallet.balance,
    currency: wallet.currency,
    engine: wallet.engine,
    memberExists: wallet.memberExists,
    maxRedeemable: wallet.maxRedeemable,
    blockedBy: wallet.blockedBy,
    minOrderValue: wallet.minOrderValue,
  };
}

const LOGGED_OUT_STATE: A2aPaymentState = {
  authenticated: false,
  balance: null,
  currency: null,
  email: null,
  engine: false,
  memberExists: false,
  maxRedeemable: null,
  blockedBy: null,
  minOrderValue: null,
  loading: false,
  loginError: null,
  payError: null,
  paySuccess: false,
  paying: false,
  appliedPoints: null,
};

export type A2aPayOutcome = 'redirected' | 'paid' | 'failed';

type A2aPaymentReturn = A2aPaymentState & {
  login: (email: string, password: string) => Promise<boolean>;
  pay: (orderId: number, checkoutToken: string, mamAmount: number) => Promise<A2aPayOutcome>;
  logout: () => Promise<void>;
  refreshBalance: (orderId?: number) => Promise<void>;
  attachOrder: (orderId: number, checkoutToken: string) => Promise<void>;
  releasePoints: (orderId: number, checkoutToken: string) => Promise<void>;
  applyPoints: (points: number) => void;
  clearPoints: () => void;
  clearLoginError: () => void;
};

/**
 * A2A loyalty state for the payment step. The balance request carries the order id so that, for
 * MAM Engine customers, the engine quotes the limit against the real order (value, promo code).
 */
export function useA2aPayment(orderId?: number): A2aPaymentReturn {
  const [state, setState] = useState<A2aPaymentState>({
    ...LOGGED_OUT_STATE,
    loading: true,
    appliedPoints: readPersistedAppliedPoints(),
  });

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    void (async () => {
      try {
        const session = await a2aService.getSession();
        rememberAlps2AlpsUserId(session.userId);
        if (!session.authenticated) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        const balanceData = await a2aService.getBalance(orderId);
        const restoredPoints = readPersistedAppliedPoints();
        setState((prev) => ({
          ...prev,
          ...walletToState(balanceData),
          email: balanceData.authenticated ? (session.email ?? prev.email) : null,
          appliedPoints: balanceData.authenticated ? (restoredPoints ?? prev.appliedPoints) : null,
          loading: false,
        }));

        if (!balanceData.authenticated) {
          persistAppliedPoints(null);
        }
      } catch {
        setState((prev) => ({ ...prev, loading: false }));
      }
    })();
  }, [orderId]);

  const clearLoginError = useCallback(() => {
    setState((prev) => (prev.loginError != null ? { ...prev, loginError: null } : prev));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, loginError: null }));
    try {
      const result = await a2aService.login(email, password);
      if (!result.success) {
        setState((prev) => ({ ...prev, loginError: A2A_LOGIN_FAILED_MESSAGE }));
        return false;
      }
      rememberAlps2AlpsUserId(result.userId);

      const balanceData = await a2aService.getBalance(orderId);
      if (!balanceData.authenticated) {
        setState((prev) => ({
          ...prev,
          ...LOGGED_OUT_STATE,
          loading: false,
          loginError: A2A_LOGIN_FAILED_MESSAGE,
        }));
        return false;
      }

      setState((prev) => ({
        ...prev,
        ...walletToState(balanceData),
        email,
        loginError: null,
      }));
      return true;
    } catch {
      setState((prev) => ({ ...prev, loginError: A2A_LOGIN_FAILED_MESSAGE }));
      return false;
    }
  }, [orderId]);

  const pay = useCallback(async (payOrderId: number, checkoutToken: string, mamAmount: number): Promise<A2aPayOutcome> => {
    setState((prev) => ({ ...prev, paying: true, payError: null }));
    try {
      const result = await a2aService.pay(payOrderId, checkoutToken, mamAmount);
      if (!result.success) {
        setState((prev) => ({ ...prev, paying: false, payError: result.error ?? 'Payment failed' }));
        return 'failed';
      }

      if (result.stripeUrl) {
        window.location.assign(result.stripeUrl);
        return 'redirected';
      }

      setState((prev) => ({ ...prev, paying: false, paySuccess: true }));
      return 'paid';
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Payment failed';
      setState((prev) => ({ ...prev, paying: false, payError: msg }));
      return 'failed';
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await a2aService.logout();
    } catch {
      // ignore
    }
    setState({ ...LOGGED_OUT_STATE });
    persistAppliedPoints(null);
  }, []);

  const refreshBalance = useCallback(async (refreshOrderId?: number) => {
    try {
      const balanceData = await a2aService.getBalance(refreshOrderId ?? orderId);
      setState((prev) => ({
        ...prev,
        ...walletToState(balanceData),
        email: balanceData.authenticated ? prev.email : null,
        appliedPoints: balanceData.authenticated ? prev.appliedPoints : null,
      }));
    } catch {
      // keep existing
    }
  }, [orderId]);

  const attachOrder = useCallback(async (attachOrderId: number, checkoutToken: string) => {
    try {
      await a2aService.attachOrder(attachOrderId, checkoutToken);
    } catch {
      // best effort: the pay call records the account again
    }
  }, []);

  const releasePoints = useCallback(async (releaseOrderId: number, checkoutToken: string) => {
    try {
      await a2aService.release(releaseOrderId, checkoutToken);
    } catch {
      // the server-side sweep returns stranded points on its own
    }
  }, []);

  const applyPoints = useCallback((points: number) => {
    const normalized = Number.isFinite(points) ? Math.round(Math.max(0, points) * 100) / 100 : 0;
    setState((prev) => ({ ...prev, appliedPoints: normalized }));
    persistAppliedPoints(normalized >= 0 ? normalized : null);
  }, []);

  const clearPoints = useCallback(() => {
    setState((prev) => ({ ...prev, appliedPoints: null }));
    persistAppliedPoints(null);
  }, []);

  return {
    ...state,
    login,
    pay,
    logout,
    refreshBalance,
    attachOrder,
    releasePoints,
    applyPoints,
    clearPoints,
    clearLoginError,
  };
}
